import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, PackageCheck, Pencil, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate } from "../../lib/api";
import { Panel } from "../../lib/ui";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const PAGE_SIZE = 10;

export const ProductionReceiptHistory = ({ canCorrect, refreshKey }) => {
  const [receipts, setReceipts] = useState([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selectedReceiptId, setSelectedReceiptId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get("/production/receipts/history");
      setReceipts(response.data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const filteredReceipts = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase();
    if (!keyword) {
      return receipts;
    }

    return receipts.filter((receipt) => [
      receipt.receipt_number,
      receipt.order_number,
      receipt.partner_name,
    ].some((value) => String(value || "").toLocaleLowerCase().includes(keyword)));
  }, [query, receipts]);
  const totalPages = Math.max(1, Math.ceil(filteredReceipts.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filteredReceipts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [query, receipts]);

  return (
    <Panel className="mt-8" data-testid="production-receipt-history-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-taupe/20 p-4">
        <div>
          <div className="font-semibold text-cocoa">Riwayat Penerimaan Produksi</div>
          <p className="mt-1 text-xs text-wmuted">
            Periksa dokumen terposting lalu koreksi satu varian bila terdapat salah input.
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Label className="sr-only" htmlFor="receipt-history-search">
            Cari riwayat penerimaan
          </Label>
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-wmuted" />
          <Input
            className="pl-9"
            data-testid="receipt-history-search"
            id="receipt-history-search"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Cari nomor penerimaan atau order"
            value={query}
          />
        </div>
      </div>
      <p className="px-4 pb-3 pt-3 text-xs text-wmuted" data-testid="receipt-history-result-count">
        Menampilkan {pageRows.length} dari {filteredReceipts.length} penerimaan
      </p>
      <div className="hidden overflow-x-auto md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>No. Penerimaan</TableHead>
              <TableHead>Order Jahit</TableHead>
              <TableHead>Konveksi</TableHead>
              <TableHead>Diposting</TableHead>
              <TableHead className="text-right">Varian</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((receipt) => (
              <TableRow data-testid={`receipt-history-row-${receipt.id}`} key={receipt.id}>
                <TableCell className="font-mono text-xs text-cocoa">
                  {receipt.receipt_number}
                </TableCell>
                <TableCell className="font-mono text-xs">{receipt.order_number}</TableCell>
                <TableCell>{receipt.partner_name}</TableCell>
                <TableCell>{fmtDate(receipt.posted_at)}</TableCell>
                <TableCell className="text-right">{receipt.item_count}</TableCell>
                <TableCell className="text-right font-semibold">
                  {receipt.total_received} pcs
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    data-testid={`receipt-history-open-${receipt.id}`}
                    onClick={() => setSelectedReceiptId(receipt.id)}
                    size="sm"
                    variant="outline"
                  >
                    <Eye className="mr-1.5 h-4 w-4" />
                    Detail
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && !pageRows.length && (
              <TableRow>
                <TableCell className="py-10 text-center text-wmuted" colSpan={7}>
                  Tidak ada penerimaan yang sesuai pencarian.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="divide-y divide-taupe/20 md:hidden" data-testid="receipt-history-mobile-list">
        {pageRows.map((receipt) => (
          <div
            className="space-y-2 p-4"
            data-testid={`receipt-history-mobile-row-${receipt.id}`}
            key={receipt.id}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-xs text-cocoa">{receipt.receipt_number}</div>
                <div className="mt-1 font-mono text-xs text-wmuted">{receipt.order_number}</div>
              </div>
              <div className="text-right text-xs text-wmuted">{fmtDate(receipt.posted_at)}</div>
            </div>
            <div className="text-sm text-cocoa">{receipt.partner_name}</div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-wmuted">
                {receipt.item_count} varian · {receipt.total_received} pcs
              </span>
              <Button
                data-testid={`receipt-history-mobile-open-${receipt.id}`}
                onClick={() => setSelectedReceiptId(receipt.id)}
                size="sm"
                variant="outline"
              >
                <Eye className="mr-1.5 h-4 w-4" />
                Detail
              </Button>
            </div>
          </div>
        ))}
        {!loading && !pageRows.length && (
          <div className="py-10 text-center text-wmuted">
            Tidak ada penerimaan yang sesuai pencarian.
          </div>
        )}
      </div>
      {filteredReceipts.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 border-t border-taupe/20 p-4"
        >
          <p className="text-xs text-wmuted" data-testid="receipt-history-pagination-info">
            Halaman {currentPage} dari {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              data-testid="receipt-history-previous-page"
              disabled={currentPage <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              size="sm"
              variant="outline"
            >
              Sebelumnya
            </Button>
            <Button
              data-testid="receipt-history-next-page"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
              size="sm"
              variant="outline"
            >
              Berikutnya
            </Button>
          </div>
        </div>
      )}
      {selectedReceiptId && (
        <ReceiptCorrectionDialog
          canCorrect={canCorrect}
          onClose={() => setSelectedReceiptId(null)}
          onCorrected={load}
          receiptId={selectedReceiptId}
        />
      )}
    </Panel>
  );
};

const ReceiptCorrectionDialog = ({ canCorrect, onClose, onCorrected, receiptId }) => {
  const [receipt, setReceipt] = useState(null);
  const [targetItem, setTargetItem] = useState(null);

  const loadReceipt = useCallback(async () => {
    try {
      const response = await api.get(`/production/receipts/${receiptId}`);
      setReceipt(response.data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  }, [receiptId]);

  useEffect(() => {
    loadReceipt();
  }, [loadReceipt]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-h-[90vh] max-w-4xl overflow-y-auto"
        data-testid="receipt-correction-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-cocoa">Detail Penerimaan & Koreksi</DialogTitle>
          <DialogDescription>
            {receipt
              ? `${receipt.receipt_number} · ${receipt.total_received} pcs`
              : "Memuat dokumen penerimaan..."}
          </DialogDescription>
        </DialogHeader>
        {receipt && (
          <>
            <div className="grid gap-2 border-y border-taupe/20 py-3 text-sm sm:grid-cols-3">
              <div>
                <div className="text-xs text-wmuted">Gudang penerimaan</div>
                <div className="font-medium text-cocoa">{receipt.warehouse_id || "-"}</div>
              </div>
              <div>
                <div className="text-xs text-wmuted">Diposting</div>
                <div className="font-medium text-cocoa">{fmtDate(receipt.posted_at)}</div>
              </div>
              <div>
                <div className="text-xs text-wmuted">Koreksi sebelumnya</div>
                <div className="font-medium text-cocoa">
                  {(receipt.correction_history || []).length} kali
                </div>
              </div>
            </div>
            <div className="space-y-3" data-testid="receipt-correction-item-list">
              {receipt.items.map((item) => (
                <div
                  className={
                    "flex flex-wrap items-center justify-between gap-3 border-b "
                    + "border-taupe/20 py-3"
                  }
                  data-testid={`receipt-correction-item-${item.variant_id}`}
                  key={item.variant_id}
                >
                  <div>
                    <div className="font-medium text-cocoa">{item.product_name}</div>
                    <div className="text-xs text-wmuted">
                      {item.color} / {item.size} · Qty dicatat: {item.current_received_qty} pcs
                    </div>
                    <div className="mt-1 text-xs text-wmuted">
                      Stok tersedia saat ini: {item.current_available_stock} pcs
                    </div>
                  </div>
                  {canCorrect && (
                    <Button
                      data-testid={`receipt-correction-open-${item.variant_id}`}
                      onClick={() => setTargetItem(item)}
                      size="sm"
                      variant="outline"
                    >
                      <Pencil className="mr-1.5 h-4 w-4" />
                      Koreksi
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {(receipt.correction_history || []).length > 0 && (
              <div
                className="border-t border-taupe/20 pt-3"
                data-testid="receipt-correction-audit-list"
              >
                <div className="mb-2 text-sm font-semibold text-cocoa">Jejak Koreksi</div>
                {receipt.correction_history.map((correction) => (
                  <div className="py-1 text-xs text-wmuted" key={correction.id}>
                    {correction.new_variant_id
                    && correction.new_variant_id !== correction.variant_id ? (
                      <>
                        {correction.color} / {correction.size} ({correction.old_qty} pcs) →
                        {" "}{correction.new_color} / {correction.new_size} ({correction.new_qty} pcs)
                      </>
                    ) : (
                      <>
                        {correction.color} / {correction.size}: {correction.old_qty} →
                        {" "}{correction.new_qty}
                      </>
                    )}
                    {" · "}{correction.by} · {correction.reason}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <div className="flex justify-end">
          <Button data-testid="receipt-correction-close" onClick={onClose} variant="outline">
            <X className="mr-1.5 h-4 w-4" />
            Tutup
          </Button>
        </div>
      </DialogContent>
      {targetItem && receipt && (
        <CorrectionFormDialog
          item={targetItem}
          onClose={() => setTargetItem(null)}
          onCorrected={async () => {
            setTargetItem(null);
            await loadReceipt();
            await onCorrected();
          }}
          orderVariants={receipt.order_variants || []}
          receipt={receipt}
        />
      )}
    </Dialog>
  );
};

const CorrectionFormDialog = ({ item, onClose, onCorrected, orderVariants, receipt }) => {
  const options = useMemo(() => orderVariants || [], [orderVariants]);
  const [newVariantId, setNewVariantId] = useState(item.variant_id);
  const [newQty, setNewQty] = useState(String(item.current_received_qty));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const selected = options.find((option) => option.variant_id === newVariantId) || null;
  const variantChanged = newVariantId !== item.variant_id;
  const proposedQty = Math.max(0, Number.parseInt(newQty, 10) || 0);
  const delta = proposedQty - Number(item.current_received_qty || 0);
  const oldStock = Number(item.current_available_stock || 0);
  // Saat pindah varian, seluruh qty lama harus dibalik dari stok varian lama.
  const reverseBlocked = variantChanged
    ? Number(item.current_received_qty || 0) > oldStock
    : (delta < 0 && Math.abs(delta) > oldStock);
  const overTarget = selected && Number(selected.order_qty || 0) > 0
    ? proposedQty > Number(selected.order_qty || 0)
    : false;
  const noChange = !variantChanged && delta === 0;

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Alasan koreksi wajib diisi");
      return;
    }
    if (noChange) {
      toast.error("Ubah warna/ukuran atau qty terlebih dahulu");
      return;
    }
    setBusy(true);
    try {
      await api.put(
        `/production/receipts/${receipt.id}/items/${item.variant_id}/correct`,
        {
          new_variant_id: newVariantId,
          new_qty: proposedQty,
          reason: reason.trim(),
          expected_updated_at: receipt.updated_at,
        },
      );
      toast.success("Koreksi penerimaan berhasil disimpan");
      await onCorrected();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="receipt-item-correction-dialog">
        <DialogHeader>
          <DialogTitle className="text-cocoa">Koreksi Penerimaan</DialogTitle>
          <DialogDescription>
            {item.product_name} · {item.color} / {item.size}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs text-wmuted">Qty tercatat</div>
              <div className="font-semibold text-cocoa" data-testid="receipt-correction-old-qty">
                {item.current_received_qty} pcs
              </div>
            </div>
            <div>
              <div className="text-xs text-wmuted">Stok varian ini</div>
              <div
                className="font-semibold text-cocoa"
                data-testid="receipt-correction-available-stock"
              >
                {item.current_available_stock} pcs
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="receipt-correction-variant">Warna / Ukuran yang benar</Label>
            <Select onValueChange={setNewVariantId} value={newVariantId}>
              <SelectTrigger
                className="mt-1"
                data-testid="receipt-correction-variant"
                id="receipt-correction-variant"
              >
                <SelectValue placeholder="Pilih varian tujuan" />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem
                    data-testid={`receipt-correction-variant-option-${option.variant_id}`}
                    key={option.variant_id}
                    value={option.variant_id}
                  >
                    {option.color} / {option.size}
                    {option.variant_id === item.variant_id ? " (saat ini)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!options.length && (
              <p className="mt-1 text-xs text-status-reject">
                Varian Order Jahit tidak tersedia untuk dipilih.
              </p>
            )}
            {variantChanged && (
              <p className="mt-1 text-xs text-amber-600" data-testid="receipt-correction-variant-note">
                Qty lama ({item.current_received_qty} pcs) akan dibalik dari varian
                {" "}{item.color}/{item.size}, lalu {proposedQty} pcs dicatat pada varian baru.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="receipt-correction-new-qty">Qty penerimaan yang benar</Label>
            <Input
              data-testid="receipt-correction-new-qty"
              id="receipt-correction-new-qty"
              min="0"
              onChange={(event) => setNewQty(event.target.value)}
              type="number"
              value={newQty}
            />
            {!variantChanged && (
              <p className="mt-1 text-xs text-wmuted" data-testid="receipt-correction-delta">
                Perubahan stok: {delta > 0 ? "+" : ""}{delta} pcs
              </p>
            )}
            {reverseBlocked && (
              <p
                className="mt-1 text-xs text-status-reject"
                data-testid="receipt-correction-stock-warning"
              >
                {variantChanged
                  ? "Stok varian lama tidak cukup untuk dibalik (sebagian sudah digunakan)."
                  : "Pengurangan melebihi stok tersedia; qty baru tidak dapat disimpan."}
              </p>
            )}
            {overTarget && (
              <p
                className="mt-1 text-xs text-status-reject"
                data-testid="receipt-correction-target-warning"
              >
                Qty melebihi target Order Jahit ({selected.order_qty} pcs) untuk varian tujuan.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="receipt-correction-reason">Alasan koreksi</Label>
            <Input
              data-testid="receipt-correction-reason"
              id="receipt-correction-reason"
              onChange={(event) => setReason(event.target.value)}
              placeholder="Contoh: salah input warna dari nota konveksi"
              value={reason}
            />
          </div>
          <p className="text-xs text-wmuted">
            Koreksi akan membalik ledger varian lama dan mencatat varian baru dengan audit. Riwayat
            penerimaan awal tidak dihapus.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            data-testid="receipt-correction-cancel"
            disabled={busy}
            onClick={onClose}
            variant="outline"
          >
            Batal
          </Button>
          <Button
            data-testid="receipt-correction-submit"
            disabled={busy || reverseBlocked || overTarget || noChange || !newVariantId}
            onClick={submit}
          >
            <PackageCheck className="mr-1.5 h-4 w-4" />
            {busy ? "Menyimpan..." : "Simpan Koreksi"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};